import { Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';
import { conversationPrinciples } from '@/karehero-app/tell-us-once/intake/principles.content';

const at = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0' />
);

// The same screens as built on dev (demo/onboarding-2-0-live-facts).
const onDev = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0-dev' />
);

/**
 * 2.0's patterns, the way Patterns 1.0 sets out 1.0's: how a question offers
 * its answers, the interactions that move, and the conversation design
 * principles they serve. Was the bottom of the Onboarding 2.0 page.
 */
export const patterns20Spec: HandoverSpec = {
  id: 'patterns-2-0',
  title: 'Patterns 4.0',
  description:
    "The pieces 2.0's conversation is built from, and when to reach for each one. What moves, and how long it takes, is on Interactions.",
  links: [
    { label: 'Onboarding 2.0', url: '/tell-us-once/flows/onboarding-2-0' },
    { label: 'Patterns 1.0', url: '/tell-us-once/flows/patterns' },
  ],
  flows: [],
  readme: (
    <Note title='Conversation design principles' variant='body2'>
      <Stack spacing={1.5}>
        {conversationPrinciples.map((p) => (
          <span key={p.name}>
            <b>{p.name}</b> {p.text}
          </span>
        ))}
      </Stack>
    </Note>
  ),
  // How a question offers its answers, worked out on this flow.
  patterns: [
    {
      id: 'pinned-checklist',
      name: 'A checklist, pinned while it is live',
      when: 'What they could tell us, ticked off as they talk. Once the chat scrolls it to the top of the screen it pins there, and it unpins when the turn ends, not when every item is ticked.',
      group: 'ask',
      // The first question's prompts, mid-recording: the one checklist on the
      // live path.
      render: onDev('help-recording'),
    },
    {
      id: 'live-captions',
      name: 'Live captions',
      when: 'What they are saying, above the bar as they say it. Off until the CC switch is on, and the same switch as Show text.',
      group: 'answer',
      render: onDev('help-recording-captions'),
    },
    {
      id: 'show-text',
      name: 'Seeing what they said',
      when: 'Show text under a voice note. One setting with the captions: flip it anywhere and it flips everywhere. Read only; anything wrong is fixed in the summary.',
      group: 'answer',
      render: onDev('help-processing'),
    },
    {
      id: 'attached-answers',
      name: 'Answers attached to the ask',
      when: 'Picking from a group, decided by what it means, not how many. Up to four, joined under the message, so the answer reads as part of the question.',
      group: 'choose',
      render: onDev('help-age'),
    },
    {
      id: 'reply-chips',
      name: 'A reply that moves it on',
      when: "Yes, Not right now, I'll do it later. Chips are shortcuts that move the conversation on, never a group to pick from. Saying it works too.",
      group: 'choose',
      render: at('help-how'),
    },
    {
      id: 'options-behind-see-options',
      name: 'Five or more, behind See options',
      when: 'Too many to attach as the question arrives without pushing it off the screen. One See options button, which opens the answers in the chat and scrolls to them.',
      group: 'choose',
      render: onDev('help-questions-open'),
    },
    {
      id: 'answered-menu',
      name: 'Once it is answered',
      when: 'Only the one they picked is left, ticked, so the history reads as decided rather than still open.',
      group: 'choose',
      render: onDev('help-age-answered'),
    },
    {
      id: 'one-place-to-tap',
      name: 'One place to tap',
      when: 'The answers, then what is not an answer as quieter rows under them. Not a chip as well, so nothing competes with the list.',
      group: 'choose',
      render: at('help-start'),
    },
  ],
  states: [],
  components: {},
};
